import { genId } from './A3Utils';
import { FIELD_LABELS, DROPDOWN_OPTIONS, DEFAULT_VALUES } from '../constants/A3Constants';

export type FieldType = 'text' | 'textarea' | 'dropdown' | 'date' | 'number' | 'row';

export interface Field {
  id: string;
  label: string;
  type: FieldType;
  value?: any;
  options?: string[];
  children?: Field[];
}

/**
 * Creates a text field
 */
export const createTextField = (label: string, value = ''): Field => ({
  id: genId(),
  label,
  type: 'text',
  value
});

/**
 * Creates a textarea field
 */
export const createTextareaField = (label: string, value = ''): Field => ({
  id: genId(),
  label,
  type: 'textarea',
  value
});

/**
 * Creates a dropdown field
 */
export const createDropdownField = (label: string, options: string[], defaultValue?: string): Field => ({
  id: genId(),
  label,
  type: 'dropdown',
  options,
  value: defaultValue || options[0]
});

/**
 * Creates a date field
 */
export const createDateField = (label: string, value = ''): Field => ({
  id: genId(),
  label,
  type: 'date',
  value
});

/**
 * Creates a row field with children
 */
export const createRowField = (label: string, children: Field[]): Field => ({
  id: genId(),
  label,
  type: 'row',
  children
});

/**
 * Creates a Cause Analysis row
 */
export const createCauseRow = (cause = '', category = '', evidence = ''): Field => 
  createRowField(FIELD_LABELS.CAUSE_ROW, [
    createTextField(FIELD_LABELS.CAUSE, cause),
    createTextField(FIELD_LABELS.CATEGORY, category),
    createTextField(FIELD_LABELS.EVIDENCE, evidence)
  ]);

/**
 * Creates a Countermeasure row
 */
export const createCountermeasureRow = (countermeasure = '', linkedCause = '', owner = ''): Field =>
  createRowField(FIELD_LABELS.COUNTERMEASURE_ROW, [
    createTextField(FIELD_LABELS.COUNTERMEASURE, countermeasure),
    createTextField(FIELD_LABELS.LINKED_CAUSE, linkedCause),
    createTextField(FIELD_LABELS.OWNER, owner)
  ]);

/**
 * Creates an Action Plan row
 */
export const createActionRow = (action = '', owner = '', dueDate = '', status = DEFAULT_VALUES.DEFAULT_STATUS): Field =>
  createRowField(FIELD_LABELS.ACTION_ROW, [
    createTextField(FIELD_LABELS.ACTION, action),
    createTextField(FIELD_LABELS.OWNER, owner),
    createTextField(FIELD_LABELS.DUE_DATE, dueDate),
    createDropdownField(FIELD_LABELS.STATUS, DROPDOWN_OPTIONS.STATUS, status)
  ]);

/**
 * Creates a Lesson row
 */
export const createLessonRow = (lesson = '', category = ''): Field =>
  createRowField(FIELD_LABELS.LESSON_ROW, [
    createTextField(FIELD_LABELS.LESSON, lesson),
    createTextField(FIELD_LABELS.CATEGORY, category)
  ]);

/**
 * Creates Project Info fields
 */
export const createProjectInfoFields = (): Field[] => [
  createTextField(FIELD_LABELS.TITLE),
  createTextField(FIELD_LABELS.INDUSTRIAL_ENGINEER),
  createTextField(FIELD_LABELS.SITE_DC),
  createDropdownField(FIELD_LABELS.REGION, DROPDOWN_OPTIONS.REGION, DEFAULT_VALUES.DEFAULT_REGION),
  createDropdownField(FIELD_LABELS.STATUS, DROPDOWN_OPTIONS.STATUS, DEFAULT_VALUES.DEFAULT_STATUS),
  createDateField(FIELD_LABELS.DATE),
  createTextField(FIELD_LABELS.VALIDATED_SAVINGS),
  createTextField(FIELD_LABELS.TAGS)
];

/**
 * Creates Define the Problem fields
 */
export const createDefineProblemFields = (): Field[] => [
  createTextareaField(FIELD_LABELS.BACKGROUND),
  createTextareaField(FIELD_LABELS.PROBLEM_STATEMENT)
];

/**
 * Creates Current State fields
 */
export const createCurrentStateFields = (): Field[] => [
  createTextareaField(FIELD_LABELS.DESCRIPTION),
  createTextField(FIELD_LABELS.METRIC_1),
  createTextField(FIELD_LABELS.METRIC_1_UNFAVORABLE),
  createTextField(FIELD_LABELS.METRIC_2),
  createTextField(FIELD_LABELS.METRIC_2_UNFAVORABLE)
];

/**
 * Creates Goal/Target State fields
 */
export const createGoalFields = (): Field[] => [
  createTextareaField(FIELD_LABELS.GOAL_STATEMENT),
  createTextField(FIELD_LABELS.TARGET_METRIC),
  createTextField(FIELD_LABELS.TARGET_DATE)
];
